import { Component, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute } from "@angular/router";
import { forkJoin, Subject } from "rxjs";
import { AppService } from "src/app/apps/app.service";
import { BroadcastService } from "src/app/broadcasts/broadcast.service";
import { FlowsService } from "src/app/flows/flows.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { UiService } from "src/app/ui/ui.service";
import { pageSizeOptions } from "src/app/shared/utils/pagination";
import { takeUntil } from "rxjs/operators";
import { FileSaverService } from "ngx-filesaver";
import { ApiService } from "src/app/api.service";
import moment from 'moment';
import { SignalsService } from "src/app/signals/signals.service";
import { UtilService } from "src/app/util/util.service";
import { LocalStorageService } from "src/app/storage/local-storage.service";

@Component({
  selector: "app-redemptions",
  templateUrl: "./redemptions.component.html",
  styleUrls: ["./redemptions.component.scss"],
})
export class RedemptionsComponent implements OnInit {
  pageSizeOptions = pageSizeOptions;
  filterForm!: FormGroup;
  currentRedemption: any = null;
  displayedColumns = [
    "name",
    "launch_date",
    "recipients",
    "tracking_period_days",
    "redemptions",
    "redemptions_pct",
    "rank",
  ];
  redemptions!: any[];
  redemptionsDataSource!: MatTableDataSource<any>;
  @ViewChild("redemptionsTable") redemptionsTable!: MatTable<any[]>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  redemptionChartOptions: any;
  regions!: any[];
  private onComponentDestroy = new Subject();
  perPage: number = 20;

  constructor(
    private route: ActivatedRoute,
    private FlowsService: FlowsService,
    private BroadcastService: BroadcastService,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private AppService: AppService,
    private uiService: UiService,
    private fileSaver: FileSaverService,
    private APIService: ApiService,
    private SignalsService: SignalsService,
    private utilSvc: UtilService,
    private localStorageSvc: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.perPage = this.localStorageSvc.pageSize || 20;

    this.filterForm = new FormGroup({
      startDate: new FormControl(this.route.snapshot.data.start),
      endDate: new FormControl(this.route.snapshot.data.end),
    });

    this.redemptionsDataSource = new MatTableDataSource(this.route.snapshot.data.redemptions.records);

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.onFilterFormChange(change);
      });

    this.regions = [];
  }

  ngAfterViewInit() {
    this.redemptionsDataSource.paginator = this.paginator;
    this.redemptionsDataSource.sort = this.sort;
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  onRedemptionSelected(row: any) {
    this.currentRedemption = row;
    this.uiService
      .waitForEvent(

        this.SignalsService.getRedemptionsReport(
          this.SessionService.currentAccountValue!.accountID,
          this.SessionService.currentAppValue!.id,
          {
            start: this.filterForm.controls.startDate.value.toISOString(),
            end: this.filterForm.controls.endDate.value.toISOString(),
            page: 0,
            perPage: 999999999,
          },
        )

      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((res) => {
        this.redemptionsDataSource = new MatTableDataSource(res.records);
      });
  }

  initRedemptions(broadcastRedemptionsDTO: any, flowRedemptionsDTO: any) {
    this.redemptions = [
      ...broadcastRedemptionsDTO.map((bRedemption: any) => {
        return {
          ...bRedemption,
          type: "qs",
          redemptions_pct: Number.parseFloat(bRedemption.redemptions_pct),
        };
      }),
      ...flowRedemptionsDTO.map((fRedemption: any) => {
        return {
          ...fRedemption,
          type: "lt",
          redemptions_pct: Number.parseFloat(fRedemption.redemptions_pct),
        };
      }),
    ];

    this.redemptions.sort((a: any, b: any) => {
      if (b.redemptions_pct > a.redemptions_pct) {
        return b;
      } else if (b.redemptions_pct === a.redemptions_pct) {
        return b.redemptions - a.redemptions;
      } else {
        return a;
      }
    });

    let count = 1;
    this.redemptions.forEach((redemption) => {
      redemption.rank = count;
      count++;
    });

    this.redemptionsDataSource = new MatTableDataSource(this.redemptions);
  }

  formatDate = (date: Date) => {
    return this.utilSvc.formatDate(date);
  }

  onFilterFormChange(change: any) {
    this.filterForm.controls.endDate.value.setHours(23, 59, 59);

    this.uiService
    .waitForEvent(
      this.SignalsService.getRedemptionsReport(
        this.SessionService.currentAccountValue!.accountID,
        this.SessionService.currentAppValue!.id,
        {
          start: change.startDate.toISOString(),
          end: this.filterForm.controls.endDate.value.toISOString(),
          page: 0,
          perPage: 999999999,
        },
      )
    )
    .pipe(takeUntil(this.onComponentDestroy))
    .subscribe(
      (res) => {
        this.redemptionsDataSource = new MatTableDataSource(res.records);
      },
      () => {
        this.NotificationService.openErrorSnackBar(
          "Error retrieving information",
        );
      },
    );

  }

  exportToExcel() {

    this.uiService
    .waitForEvent(

      this.APIService.get(
        `account-apps-device-signal/${this.SessionService.currentAccountValue!.accountID}/app/${this.SessionService.currentAppValue!.id}/redemptions`,
        {
          params: {
            start: this.filterForm.controls.startDate.value.toISOString(),
            end: this.filterForm.controls.endDate.value.toISOString(),
            page: 0,
            perPage: 999999999,
            forExport: true
          },
          'responseType': 'blob' as 'xlsx'
        },
      )

    )
    .pipe(takeUntil(this.onComponentDestroy))
    .subscribe(
      res => {
        this.fileSaver.save((<any>res), moment().format("[Redemptions-]YYYY-MM-DD-HH-mm-ss[.xlsx]"));
      },
      error => {
          console.error('download error:', error)
      }, 
      () => {
          //console.log('Completed file download.')
      }
    );

  }

  initRedemptionData(redemptionData: any) {
    this.redemptionChartOptions = {
      grid: {
        containLabel: true,
        left: 20,
        top: 20,
        right: 20,
        bottom: 20,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      xAxis: {
        type: "time",
        axisLine: {
          show: true,
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
      },

      series: [
        {
          name: "Redemptions",
          data: redemptionData.map((redemptionRange: any) => {
            return [redemptionRange.dates, redemptionRange.redeems];
          }),

          type: "line",
          smooth: true,
          symbol: "circle",
          symbolSize: 10,
          lineStyle: {
            shadowOffsetX: -5,
            shadowColor: "rgba(0, 0, 0, 0.5)",
            shadowBlur: 10,
            shadowOffsetY: 10,
          },
        },
      ],
    };
  }

  onPageChange(e: PageEvent){
    this.localStorageSvc.pageSize = e.pageSize;
    this.perPage = e.pageSize;
  }
}
